import React, { useState, useEffect } from "react";
import Icon from "../AppIcon";
import Button from "../ui/Button";
import Input from "../ui/Input";

/**
 * Seniority Bands Configuration Modal
 * Allows configuration of service length bands with multipliers
 */
const SeniorityBandsModal = ({
  isOpen,
  onClose,
  bands = [],
  onSave,
  readonly = false,
}) => {
  const [localBands, setLocalBands] = useState([]);
  const [errors, setErrors] = useState([]);

  const defaultBands = [
    { minMonths: 0, maxMonths: 6, multiplier: 1.0, name: "Probationary" },
    { minMonths: 6, maxMonths: 12, multiplier: 1.2, name: "Standard" },
    { minMonths: 12, maxMonths: 24, multiplier: 1.4, name: "Established" },
 { minMonths: 24, maxMonths: null, multiplier: 1.6, name: "Senior" },
  ];

  useEffect(() => {
  if (isOpen) {
   setLocalBands(bands.length > 0 ? [...bands] : [...defaultBands]);
      setErrors([]);
 }
  }, [isOpen, bands]);

  const validateBands = () => {
    const newErrors = [];
    
    // Check for empty bands
    if (localBands.length === 0) {
      newErrors.push("At least one seniority band is required.");
      return newErrors;
    }

    // Sort by minMonths for validation
    const sorted = [...localBands].sort((a, b) => a.minMonths - b.minMonths);
    
    // Check each band
    sorted.forEach((band, index) => {
      if (!band.name || band.name.trim() === "") {
 newErrors.push(`Band ${index + 1}: Name is required.`);
}
      if (band.multiplier <= 0) {
     newErrors.push(`Band ${index + 1}: Multiplier must be greater than 0.`);
   }
      if (band.minMonths < 0) {
        newErrors.push(`Band ${index + 1}: Min months cannot be negative.`);
      }
      if (band.maxMonths !== null && band.maxMonths <= band.minMonths) {
 newErrors.push(`Band ${index + 1}: Max months must be greater than min months.`);
      }
    });

    // Check for gaps or overlaps
    for (let i = 0; i < sorted.length - 1; i++) {
      const current = sorted[i];
   const next = sorted[i + 1];
      
  if (current.maxMonths !== null && current.maxMonths !== next.minMonths) {
        if (current.maxMonths > next.minMonths) {
      newErrors.push(`Bands overlap: "${current.name}" ends at ${current.maxMonths} but "${next.name}" starts at ${next.minMonths}.`);
 } else {
    newErrors.push(`Gap between bands: "${current.name}" ends at ${current.maxMonths} but "${next.name}" starts at ${next.minMonths}.`);
        }
      }
    }

    // Last band should have no max
    const lastBand = sorted[sorted.length - 1];
    if (lastBand.maxMonths !== null) {
 newErrors.push("The last band should have no maximum (unlimited) to cover all long-serving employees.");
    }

    return newErrors;
  };

  const addBand = () => {
    const sorted = [...localBands].sort((a, b) => a.minMonths - b.minMonths);
    const lastBand = sorted[sorted.length - 1];
    
    // New band starts where the last one ends (or at 0 if no bands)
    const newMinMonths = lastBand && lastBand.maxMonths !== null 
      ? lastBand.maxMonths 
 : (lastBand ? lastBand.minMonths + 12 : 0);
    
    setLocalBands([
...localBands,
      {
   minMonths: newMinMonths,
        maxMonths: null,
        multiplier: 1.0,
        name: `Band ${localBands.length + 1}`,
      },
    ]);
  };

  const removeBand = (index) => {
    if (localBands.length <= 1) return;
    setLocalBands(localBands.filter((_, i) => i !== index));
  };

  const updateBand = (index, field, value) => {
const updated = [...localBands];
    let parsedValue = value;
    
    if (field === "minMonths" || field === "maxMonths") {
 parsedValue = value === "" || value === null ? null : parseInt(value, 10);
      if (field === "maxMonths" && parsedValue !== null && isNaN(parsedValue)) {
        parsedValue = null;
      }
    } else if (field === "multiplier") {
      parsedValue = parseFloat(value) || 1.0;
    }
    
    updated[index] = { ...updated[index], [field]: parsedValue };
  setLocalBands(updated);
  };

  const handleApplyDefaults = () => {
  setLocalBands([...defaultBands]);
    setErrors([]);
  };

  const handleSave = () => {
    const validationErrors = validateBands();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    // Sort by minMonths before saving
    const sorted = [...localBands].sort((a, b) => a.minMonths - b.minMonths);
    onSave(sorted);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
className="absolute inset-0 bg-black/50" 
   onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-card rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-border">
        {/* Header */}
 <div className="flex items-center justify-between p-4 border-b border-border">
      <div className="flex items-center gap-2">
   <Icon name="Award" size={20} className="text-primary" />
   <h2 className="text-lg font-semibold">Configure Seniority Bands</h2>
   </div>
 <button 
      onClick={onClose}
  className="p-1 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
<p className="text-sm text-muted-foreground mb-4">
   Configure how length of service affects TRONC distribution.
         Employees in higher bands receive a multiplied share based on their months of service.
     </p>

          {/* Quick Apply Default Bands */}
      <div className="mb-4">
            <Button 
              variant="outline" 
     size="sm" 
              onClick={handleApplyDefaults}
              disabled={readonly}
            >
   <Icon name="Zap" size={14} />
  Apply Default Bands
          </Button>
    </div>

          {/* Errors */}
          {errors.length > 0 && (
   <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-start gap-2">
       <Icon name="AlertCircle" size={16} className="text-error shrink-0 mt-0.5" />
     <div className="text-sm text-error space-y-1">
       {errors.map((error, i) => (
       <p key={i}>{error}</p>
           ))}
         </div>
         </div>
            </div>
     )}

          {/* Bands List */}
          <div className="space-y-3">
         {localBands.map((band, index) => (
    <div key={index} className="p-3 bg-muted/50 border border-border rounded-lg">
                <div className="grid grid-cols-12 gap-3 items-end">
  {/* Name */}
        <div className="col-span-4">
           <label className="block text-xs font-medium text-muted-foreground mb-1">
         Band Name
        </label>
               <Input
       value={band.name}
   onChange={(e) => updateBand(index, "name", e.target.value)}
       placeholder="e.g., Senior"
    disabled={readonly}
         />
</div>

      {/* Min Months */}
     <div className="col-span-2">
<label className="block text-xs font-medium text-muted-foreground mb-1">
  From (mo)
     </label>
          <Input
     type="number"
   value={band.minMonths}
          onChange={(e) => updateBand(index, "minMonths", e.target.value)}
        min={0}
        disabled={readonly}
         />
    </div>

   {/* Max Months */}
 <div className="col-span-2">
              <label className="block text-xs font-medium text-muted-foreground mb-1">
       To (mo)
   </label>
           <Input
type="number"
             value={band.maxMonths ?? ""}
          onChange={(e) => updateBand(index, "maxMonths", e.target.value)}
placeholder="?"
 min={band.minMonths + 1}
           disabled={readonly}
    />
 </div>

  {/* Multiplier */}
   <div className="col-span-2">
      <label className="block text-xs font-medium text-muted-foreground mb-1">
Multiplier
</label>
     <Input
    type="number"
    step="0.1"
    value={band.multiplier}
         onChange={(e) => updateBand(index, "multiplier", e.target.value)}
           min={0.1}
   disabled={readonly}
      />
       </div>

         {/* Remove Button */}
 <div className="col-span-2 flex justify-end">
       {!readonly && localBands.length > 1 && (
    <button
   type="button"
   onClick={() => removeBand(index)}
           className="p-2 text-muted-foreground hover:text-error hover:bg-error/10 rounded-lg transition-colors"
                   title="Remove band"
         >
         <Icon name="Trash2" size={16} />
           </button>
        )}
                </div>
       </div>
        </div>
            ))}
       </div>

        {/* Add Band Button */}
{!readonly && (
            <div className="mt-3">
              <Button variant="outline" size="sm" onClick={addBand}>
           <Icon name="Plus" size={14} />
  Add Band
      </Button>
  </div>
)}

          {/* Preview */}
   <div className="mt-4 p-3 bg-muted rounded-lg border border-border">
    <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
              <Icon name="Eye" size={14} />
Preview
            </h5>
      <div className="space-y-1">
   {[...localBands]
 .sort((a, b) => a.minMonths - b.minMonths)
       .map((band, i) => (
   <div key={i} className="flex justify-between text-sm">
     <span className="text-muted-foreground">
         {band.name}: {band.minMonths}
  {band.maxMonths !== null ? `-${band.maxMonths}` : "+"} months
           </span>
  <span className="font-mono font-medium">
       {band.multiplier.toFixed(1)}� share
</span>
          </div>
          ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Example: If base share is �100, an employee in the "{localBands[localBands.length - 1]?.name || 'Senior'}" band 
  would receive �{(100 * (localBands[localBands.length - 1]?.multiplier || 1)).toFixed(2)}
            </p>
   </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-border bg-muted/30">
<Button variant="outline" onClick={onClose}>
      Cancel
  </Button>
          {!readonly && (
<Button onClick={handleSave}>
     <Icon name="Check" size={16} />
     Save Bands
            </Button>
 )}
        </div>
      </div>
    </div>
  );
};

export default SeniorityBandsModal;
